import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUpload } from 'react-icons/fa';
import Select from 'react-select/async';
import dayjs from 'dayjs';
import { Country, State, City } from 'country-state-city';

const API_URL = '/api';

const initialFields = {
  locationCode: '', locationName: '', locationTypeID: '', parentLocationID: '',
  country: '', state: '', city: '', postalCode: '', addressLine1: '', addressLine2: '',
  status: 1, createdBy: 1, updatedBy: 1
};

const LocationFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [formData, setFormData] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [locationTypes, setLocationTypes] = useState([]);
  const [parentLocations, setParentLocations] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [filePreview, setFilePreview] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchLocationData = useCallback(async () => {
    try {
      setLoadingDetails(true);
      const response = await fetch(`${API_URL}/locations/${initialData}`);
      if (response.ok) {
        const location = await response.json();
        console.log('Location data received:', {
          locationID: location.LocationID,
          status: location.Status,
          statusType: typeof location.Status
        });
        
        setFormData({
          locationCode: location.LocationCode,
          locationName: location.LocationName,
          locationTypeID: location.LocationTypeID,
          parentLocationID: location.ParentLocationID || '',
          country: location.Country,
          state: location.State,
          city: location.City,
          postalCode: location.PostalCode,
          addressLine1: location.AddressLine1,
          addressLine2: location.AddressLine2 || '',
          status: location.Status ? 1 : 0, // Convert boolean to number
          createdBy: location.CreatedBy,
          updatedBy: location.UpdatedBy
        });
        setFileName(location.FileName || '');
        setFileType(location.FileType || '');
        setFilePreview({});
      }
    } catch (error) {
      console.error('Error fetching location data:', error);
    } finally {
      setLoadingDetails(false);
    }
  }, [initialData]);

  // Reset form state on close
  useEffect(() => {
    if (!open) {
      setFormData(initialFields);
      setErrors({});
      setSelectedFile(null);
      setFileName('');
      setFileType('');
      setFilePreview({});
      setLoadingDetails(false);
    }
  }, [open]);

  // Fetch data on open
  useEffect(() => {
    if (open) {
      fetchLocationTypes();
      fetchParentLocations();
      if (!isEdit) {
        fetchNextLocationCode();
      }
    }
  }, [open, isEdit]);

  useEffect(() => {
    if (isEdit && initialData) {
      fetchLocationData();
    }
  }, [isEdit, initialData, fetchLocationData]);

  const fetchLocationTypes = async () => {
    try {
      setIsLoadingTypes(true);
      const response = await fetch(`${API_URL}/location-types`);
      if (response.ok) {
        const data = await response.json();
        setLocationTypes(data.locationTypes || []);
      }
    } catch (error) {
      console.error('Error fetching location types:', error);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const fetchParentLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await fetch(`${API_URL}/locations`);
      if (response.ok) {
        const data = await response.json();
        setParentLocations(data || []);
      }
    } catch (error) {
      console.error('Error fetching parent locations:', error);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const fetchNextLocationCode = async () => {
    try {
      setIsLoadingCode(true);
      const response = await fetch(`${API_URL}/locationlatestcode`);
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, locationCode: data.nextCode }));
      }
    } catch (error) {
      console.error('Error fetching next location code:', error);
      setFormData(prev => ({ ...prev, locationCode: 'LOC-01' }));
    } finally {
      setIsLoadingCode(false);
    }
  };

  // Load countries using country-state-city package
  const loadCountries = async (inputValue) => {
    const countries = Country.getAllCountries();
    return countries
      .filter(country =>
        country.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        country.isoCode.toLowerCase().includes(inputValue.toLowerCase())
      )
      .map(country => ({
        value: country.name,
        label: `${country.name} (${country.isoCode})`
      }));
  };

  // Load states based on selected country
  const loadStates = async (inputValue) => {
    if (!formData.country) return [];
    
    // Find country code from country name
    const country = Country.getAllCountries().find(c => c.name === formData.country);
    if (!country) return [];
    
    const states = State.getStatesOfCountry(country.isoCode);
    return states
      .filter(state =>
        state.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        state.isoCode.toLowerCase().includes(inputValue.toLowerCase())
      )
      .map(state => ({
        value: state.name,
        label: `${state.name} (${state.isoCode})`
      }));
  };

  // Load cities based on selected state and country
  const loadCities = async (inputValue) => {
    if (!formData.country || !formData.state) return [];
    
    // Find country code from country name
    const country = Country.getAllCountries().find(c => c.name === formData.country);
    if (!country) return [];
    
    // Find state code from state name
    const state = State.getStatesOfCountry(country.isoCode).find(s => s.name === formData.state);
    if (!state) return [];
    
    const cities = City.getCitiesOfState(country.isoCode, state.isoCode);
    return cities
      .filter(city =>
        city.name.toLowerCase().includes(inputValue.toLowerCase())
      )
      .map(city => ({
        value: city.name,
        label: city.name
      }));
  };

  // Get default options for states when country is selected
  const getDefaultStates = () => {
    if (!formData.country) return [];
    
    const country = Country.getAllCountries().find(c => c.name === formData.country);
    if (!country) return [];
    
    const states = State.getStatesOfCountry(country.isoCode);
    return states.map(state => ({
      value: state.name,
      label: `${state.name} (${state.isoCode})`
    }));
  };

  // Get default options for cities when state is selected
  const getDefaultCities = () => {
    if (!formData.country || !formData.state) return [];
    
    const country = Country.getAllCountries().find(c => c.name === formData.country);
    if (!country) return [];
    
    const state = State.getStatesOfCountry(country.isoCode).find(s => s.name === formData.state);
    if (!state) return [];
    
    const cities = City.getCitiesOfState(country.isoCode, state.isoCode);
    return cities.map(city => ({
      value: city.name,
      label: city.name
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Reset dependent fields when country or state changes
    if (field === 'country') {
      setFormData(prev => ({ ...prev, state: '', city: '' }));
    } else if (field === 'state') {
      setFormData(prev => ({ ...prev, city: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      setFileType(file.type);
      setFilePreview(prev => ({
        ...prev,
        attachment: URL.createObjectURL(file)
      }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.locationName) errs.locationName = 'Required';
    if (!formData.locationTypeID) errs.locationTypeID = 'Required';
    if (!formData.country) errs.country = 'Required';
    if (!formData.state) errs.state = 'Required';
    if (!formData.city) errs.city = 'Required';
    if (!formData.postalCode) errs.postalCode = 'Required';
    if (!formData.addressLine1) errs.addressLine1 = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // Prevent double submission
    if (!validate()) return;
    
    const submitData = new FormData();
    
    // Add all form fields with proper field names matching backend expectations
    submitData.append('locationCode', formData.locationCode);
    submitData.append('locationName', formData.locationName);
    submitData.append('locationTypeID', formData.locationTypeID);
    submitData.append('parentLocationID', formData.parentLocationID || '');
    submitData.append('country', formData.country || '');
    submitData.append('state', formData.state || '');
    submitData.append('city', formData.city || '');
    submitData.append('postalCode', formData.postalCode || '');
    submitData.append('addressLine1', formData.addressLine1 || '');
    submitData.append('addressLine2', formData.addressLine2 || '');
    submitData.append('status', formData.status ? '1' : '0'); // Convert to string for FormData
    submitData.append('createdBy', formData.createdBy);
    submitData.append('updatedBy', formData.updatedBy);
    
    // Add file if selected
    if (selectedFile) {
      submitData.append('file', selectedFile);
      submitData.append('fileName', fileName);
      submitData.append('fileType', fileType);
    }
    
    // Debug: Log the form data being sent
    console.log('Form data being submitted:', {
      isEdit,
      locationCode: formData.locationCode,
      locationName: formData.locationName,
      locationTypeID: formData.locationTypeID,
      status: formData.status,
      statusType: typeof formData.status,
      statusString: formData.status ? '1' : '0',
      hasFile: !!selectedFile,
      fileName: fileName
    });
    
    await onSubmit(submitData);
  };

  if (!open) return null;

  if (loadingDetails || isLoadingCode) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <span className="text-[#2b6777] text-lg font-semibold">Loading...</span>
        </div>
      </div>
    );
  }

  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-5xl p-4 sm:p-8 relative overflow-y-auto max-h-[95vh]">
      <button className="absolute top-4 right-4 text-[#2b6777] hover:text-[#52ab98]" onClick={onClose}>
        <FaTimes size={20} />
      </button>
      
      <h2 className="text-xl sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6 text-center">
        {isEdit ? 'Edit Location' : 'Create New Location'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Location Details Section */}
        <div className='bg-white p-3 sm:p-4 rounded-xl shadow-md'>
          <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-2">Location Details</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {/* Location Code */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Location Code
              </label>
              <div className="w-full sm:w-3/4">
                <input 
                  type="text"
                  value={isLoadingCode ? 'Loading...' : formData.locationCode}
                  readOnly 
                  className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700" 
                />
              </div>
            </div>
            
            {/* Location Name */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Location Name * 
              </label>
              <div className="w-full sm:w-3/4">
                <input 
                  type="text"
                  autoComplete="off"
                  value={formData.locationName} 
                  onChange={(e) => handleInputChange('locationName', e.target.value)} 
                  className={`w-full border rounded px-3 py-2 ${errors.locationName ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                />
                {errors.locationName && <span className="text-xs text-red-500 mt-1 block">{errors.locationName}</span>}
              </div>
            </div>
            
            {/* Location Type */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Location Type *
              </label>
              <div className="w-full sm:w-3/4">
                <select
                  value={formData.locationTypeID}
                  onChange={(e) => handleInputChange('locationTypeID', e.target.value)}
                  className={`w-full border rounded px-3 py-2 ${errors.locationTypeID ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                  disabled={isLoadingTypes}
                >
                  <option value="">Select Location Type</option>
                  {locationTypes.map(type => (
                    <option key={type.LocationTypeID} value={type.LocationTypeID}>
                      {type.LocationTypeName}
                    </option>
                  ))}
                </select>
                {errors.locationTypeID && <span className="text-xs text-red-500 mt-1 block">{errors.locationTypeID}</span>}
              </div>
            </div>
            
            {/* Parent Location */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Parent Location
              </label>
              <div className="w-full sm:w-3/4">
                <select
                  value={formData.parentLocationID}
                  onChange={(e) => handleInputChange('parentLocationID', e.target.value)}
                  className="w-full border border-[#c8d8e4] rounded px-3 py-2"
                  disabled={isLoadingLocations}
                >
                  <option value="">Select Parent Location</option>
                  {parentLocations.map(location => (
                    <option key={location.LocationID} value={location.LocationID}>
                      {location.LocationName} ({location.LocationCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Status */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Status *
              </label>
              <div className="w-full sm:w-3/4">
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                  className="w-full border border-[#c8d8e4] rounded px-3 py-2"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        
        {/* Address Information Section */}
        <div className='bg-white p-3 sm:p-4 rounded-xl shadow-md'>
          <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-2">Address Information</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {/* Country */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Country *
              </label>
              <div className="w-full sm:w-3/4">
                <Select
                  loadOptions={loadCountries}
                  defaultOptions
                  value={formData.country ? { value: formData.country, label: formData.country } : null}
                  onChange={(option) => handleInputChange('country', option?.value || '')}
                  className="w-full"
                  placeholder="Search country..."
                  isClearable
                  noOptionsMessage={() => "No countries found. Try typing at least 2 characters."}
                />
                {errors.country && <span className="text-xs text-red-500 mt-1 block">{errors.country}</span>}
              </div>
            </div>
            
            {/* State/Province */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                State/Province *
              </label>
              <div className="w-full sm:w-3/4">
                <Select
                  loadOptions={loadStates}
                  defaultOptions={getDefaultStates()}
                  value={formData.state ? { value: formData.state, label: formData.state } : null}
                  onChange={(option) => handleInputChange('state', option?.value || '')}
                  className="w-full"
                  placeholder={formData.country ? "Search state..." : "Please select a country first"}
                  isClearable
                  isDisabled={!formData.country}
                  noOptionsMessage={() => {
                    if (!formData.country) return "Please select a country first";
                    return "No states found. Try typing at least 2 characters.";
                  }}
                />
                {errors.state && <span className="text-xs text-red-500 mt-1 block">{errors.state}</span>}
              </div>
            </div>
            
            {/* City */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                City *
              </label>
              <div className="w-full sm:w-3/4">
                <Select
                  loadOptions={loadCities}
                  defaultOptions={getDefaultCities()}
                  value={formData.city ? { value: formData.city, label: formData.city } : null}
                  onChange={(option) => handleInputChange('city', option?.value || '')}
                  className="w-full"
                  placeholder={formData.country && formData.state ? "Search city..." : "Please select country and state first"}
                  isClearable
                  isDisabled={!formData.country || !formData.state}
                  noOptionsMessage={() => {
                    if (!formData.country) return "Please select a country first";
                    if (!formData.state) return "Please select a state first";
                    return "No cities found. Try typing at least 2 characters.";
                  }}
                />
                {errors.city && <span className="text-xs text-red-500 mt-1 block">{errors.city}</span>}
              </div>
            </div>
            
            {/* Postal/Zip Code */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Postal/Zip Code *
              </label>
              <div className="w-full sm:w-3/4">
                <input
                  type="text"
                  autoComplete="off"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  className={`w-full border rounded px-3 py-2 ${errors.postalCode ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                />
                {errors.postalCode && <span className="text-xs text-red-500 mt-1 block">{errors.postalCode}</span>}
              </div>
            </div>
            
            {/* Address Line 1 */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center sm:col-span-2'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Address Line 1 *
              </label>
              <div className="w-full sm:w-3/4">
                <input
                  type="text"
                  autoComplete="off"
                  value={formData.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  className={`w-full border rounded px-3 py-2 ${errors.addressLine1 ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                />
                {errors.addressLine1 && <span className="text-xs text-red-500 mt-1 block">{errors.addressLine1}</span>}
              </div>
            </div>
            
            {/* Address Line 2 */}
            <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center sm:col-span-2'>
              <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4 text-center sm:text-left whitespace-nowrap">
                Address Line 2
              </label>
              <div className="w-full sm:w-3/4">
                <input
                  type="text"
                  autoComplete="off"
                  value={formData.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  className="w-full border border-[#c8d8e4] rounded px-3 py-2"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Documents Section */}
        <div className='bg-white p-3 sm:p-4 rounded-xl shadow-md'>
          <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-2">Documents</h3>
          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-[#2b6777]">Address Proof Document (Optional)</label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="hidden"
                id="attachment-upload"
              />
              <label
                htmlFor="attachment-upload"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-[#52ab98] text-white rounded cursor-pointer hover:bg-[#2b6777] w-full sm:w-auto"
              >
                <FaUpload /> Upload Document
              </label>
              
              <div className="flex items-center gap-2 mt-2 sm:mt-0">
                {filePreview.attachment && (
                  <img src={filePreview.attachment} alt="Document preview" className="w-10 h-10 object-cover rounded" />
                )}
                {fileName && (
                  <span className="text-sm text-gray-600 truncate max-w-xs">{fileName}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-4 sm:mt-6">
          <button 
            type="button" 
            className="px-4 py-2 rounded bg-gray-200 text-[#2b6777] font-semibold w-full sm:w-auto order-2 sm:order-1"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto order-1 sm:order-2"
            disabled={loading || isLoadingCode}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Location' : 'Create Location')}
          </button>
        </div>
      </form>
    </div>
  </div>
);
};

export default LocationFormModal; 